function formatTime(seconds) {
  if (isNaN(seconds) || seconds === Infinity) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

class AudioPlayer extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const fallback = this.querySelector('audio');
    if (fallback) fallback.style.display = 'none';

    this._audio = fallback || document.createElement('audio');

    const src = this.getAttribute('src') || fallback?.getAttribute('src');
    if (src) this._audio.src = src;

    this._playPauseButton = this.querySelector('.js-play-pause');
    this._playIcon = this.querySelector('.js-icon-play');
    this._pauseIcon = this.querySelector('.js-icon-pause');
    this._stateLabel = this.querySelector('.js-state-label');
    this._timeline = this.querySelector('.js-timeline');

    this._currentTimeWrapper = this.querySelector('.current-time-wrapper');
    this._currentTimeVisible = this.querySelector('.js-current-time');
    this._currentTimeAccessible = this.querySelector('#current-time-accessible');

    this._durationWrapper = this.querySelector('.duration-wrapper');
    this._durationVisible = this.querySelector('.js-duration');
    this._durationAccessible = this.querySelector('#duration-accessible');

    this._isSeeking = false;

    this._bindEvents();
  }

  _bindEvents() {
    this._playPauseButton.addEventListener('click', () => {
      if (this._audio.paused) {
        this._audio.play();
      } else {
        this._audio.pause();
      }
    });

    this._audio.addEventListener('play', () => this._updatePlayState(true));
    this._audio.addEventListener('pause', () => this._updatePlayState(false));
    this._audio.addEventListener('timeupdate', () => {
      this._onTimeUpdate();
      this._updateTimelineGradient();
    });
    this._audio.addEventListener('loadedmetadata', () => {
      this._onLoadedMetadata();
      this._updateTimelineGradient();
    });
    this._audio.addEventListener('ended', () => {
      this._audio.currentTime = 0;
      this._audio.pause();
    });

    this._timeline.addEventListener('input', () => {
      this._isSeeking = true;
      const newTime = parseFloat(this._timeline.value);
      this._updateCurrentTime(newTime);
      this._updateTimelineGradient();
    });

    this._timeline.addEventListener('change', () => {
      const newTime = parseFloat(this._timeline.value);
      this._audio.currentTime = newTime;
      this._isSeeking = false;
      this._updateTimelineGradient();
    });
  }

  _updateTimelineGradient() {
    const value = this._timeline.value;
    const max = this._timeline.max || 0;

    // Avoid division by zero
    const percent = max > 0 ? (value / max) * 100 : 0;

    this._timeline.style.background =
      `linear-gradient(to right, #ADADAD ${percent}%, #E6E6E6 ${percent}%)`;
  }

  _updatePlayState(isPlaying) {
    this._playPauseButton.setAttribute('aria-pressed', String(isPlaying));

    // Update the hidden text using the existing data attributes
    this._stateLabel.textContent = isPlaying
      ? this._playPauseButton.dataset.labelPause
      : this._playPauseButton.dataset.labelPlay;

    // Swap icons using `hidden` instead of display:none
    this._playIcon.hidden = isPlaying;
    this._pauseIcon.hidden = !isPlaying;
  }

  _onLoadedMetadata() {
    const duration = this._audio.duration;
    this._timeline.max = isNaN(duration) ? 0 : duration;
    this._updateDuration(duration);
  }

  _updateCurrentTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    const minuteLabel =
      minutes === 1
        ? this._currentTimeVisible.dataset.minute
        : this._currentTimeVisible.dataset.minutes;

    const secondLabel =
      secs === 1
        ? this._currentTimeVisible.dataset.second
        : this._currentTimeVisible.dataset.seconds;

    this._currentTimeVisible.textContent = formatTime(seconds);

    // Combined accessible string
    this._currentTimeAccessible.textContent =
      `${this.querySelector('#current-time-label').textContent} ${minutes} ${minuteLabel} ${secs} ${secondLabel}`;
  }

  _updateDuration(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    const minuteLabel =
      minutes === 1
        ? this._durationVisible.dataset.minute
        : this._durationVisible.dataset.minutes;

    const secondLabel =
      secs === 1
        ? this._durationVisible.dataset.second
        : this._durationVisible.dataset.seconds;

    this._durationVisible.textContent = formatTime(seconds);

    this._durationAccessible.textContent =
      `${this.querySelector('#duration-label').textContent} ${minutes} ${minuteLabel} ${secs} ${secondLabel}`;
  }

  _onTimeUpdate() {
    if (!this._isSeeking) {
      const seconds = this._audio.currentTime;
      this._timeline.value = seconds;
      this._updateCurrentTime(seconds);
    }
  }
}

customElements.define('audio-player', AudioPlayer);